<h1 mat-dialog-title>Edit Label (v{{ assetLabel.versionNumber }})</h1>

<mat-dialog-content>
  @if (availableFields.length > 0) {
    <div class="add-field-row">
      <mat-form-field appearance="outline">
        <mat-label>Add field</mat-label>
        <mat-select [(value)]="selectedNewField">
          @for (field of availableFields; track field) {
            <mat-option [value]="field">
              {{ field }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
      <button mat-icon-button color="primary" (click)="addField()" [disabled]="!selectedNewField">
        <mat-icon>add</mat-icon>
      </button>
    </div>
  }

  <form [formGroup]="form" class="label-form">
    @for (field of labelFields; track field; let i = $index) {
      <ng-container *ngTemplateOutlet="topLevelField; context: { field: field, index: i }"></ng-container>
    }
  </form>
</mat-dialog-content>

<!-- Top-level fields -->
<ng-template #topLevelField let-field="field" let-index="index">
  <div class="object-field" [id]="'field-' + field.key" [class.highlighted]="highlightedPath === 'field-' + field.key">
    <div class="object-header">
      <span class="object-title">{{ field.key }}</span>
      <div class="field-actions">
        @if (field.type === 'objectArray') {
          <button mat-icon-button color="primary" (click)="addArrayItem(field)" matTooltip="Add item">
            <mat-icon>add</mat-icon>
          </button>
        }
        <button mat-icon-button color="warn" (click)="removeTopLevelField(index)" matTooltip="Remove" >
          <mat-icon>delete</mat-icon>
        </button>
      </div>
    </div>
    <div class="object-content">
      @switch (field.type) {
        @case ('primitive') {
          <mat-form-field appearance="outline" class="field-input">
            <input matInput [formControl]="getControl(field.path)"  />
          </mat-form-field>
        }
        @case ('object') {
          @if (isDictionaryField(field)) {
            <div class="add-item-row">
              <mat-form-field appearance="outline" class="field-input">
                <mat-label>Add custom key</mat-label>
                <input matInput [(ngModel)]="newCustomKeys[field.path]" [ngModelOptions]="{standalone: true}"
                  (keyup.enter)="addCustomKey(field)" placeholder="e.g. myCustomField" />
              </mat-form-field>
              <button mat-icon-button color="primary" (click)="addCustomKey(field)" [disabled]="!newCustomKeys[field.path]">
                <mat-icon>add</mat-icon>
              </button>
            </div>
          }
          @for (child of field.children; track child; let j = $index) {
            <ng-container *ngTemplateOutlet="schemaField; context: { field: child, index: j, fields: field.children }"></ng-container>
          }
        }
        @case ('primitiveArray') {
          <ng-container *ngTemplateOutlet="primitiveArrayContent; context: { field: field }"></ng-container>
        }
        @case ('objectArray') {
          @for (item of field.children; track item; let j = $index) {
            <ng-container *ngTemplateOutlet="objectArrayItem; context: { field: item, index: j, fields: field.children }"></ng-container>
          }
        }
      }
    </div>
  </div>
</ng-template>

<!-- Primitive array content with add input -->
<ng-template #primitiveArrayContent let-field="field">
  <div class="add-item-row">
    <mat-form-field appearance="outline" class="field-input">
      <mat-label>Add item</mat-label>
      <input matInput [(ngModel)]="newArrayItemValues[field.path]" [ngModelOptions]="{standalone: true}"
        (keyup.enter)="addArrayItem(field)" />
    </mat-form-field>
    <button mat-icon-button color="primary" (click)="addArrayItem(field)" [disabled]="!newArrayItemValues[field.path]">
      <mat-icon>add</mat-icon>
    </button>
  </div>
  @for (item of field.children; track item; let j = $index) {
    <div class="field-row" [id]="'item-' + item.path" [class.highlighted]="highlightedPath === 'item-' + item.path">
      <mat-form-field appearance="outline" class="field-input">
        <input matInput [formControl]="getControl(item.path)" readonly />
      </mat-form-field>
      <button mat-icon-button color="warn" (click)="removeSubField(field.children, j)" matTooltip="Remove">
        <mat-icon>delete</mat-icon>
      </button>
    </div>
  }
</ng-template>

<!-- Schema-defined fields (deletable) -->
<ng-template #schemaField let-field="field" let-index="index" let-fields="fields">
  @switch (field.type) {
    @case ('primitive') {
      <div class="field-row">
        <mat-form-field appearance="outline" class="field-input">
          <mat-label>{{ field.key }}</mat-label>
          <input matInput [formControl]="getControl(field.path)"  />
        </mat-form-field>
        <button mat-icon-button color="warn" (click)="removeSubField(fields, index)" matTooltip="Remove" >
          <mat-icon>delete</mat-icon>
        </button>
      </div>
    }
    @case ('object') {
      <div class="nested-object">
        <div class="nested-header">
          <span class="nested-title">{{ field.key }}</span>
          <button mat-icon-button color="warn" (click)="removeSubField(fields, index)" matTooltip="Remove">
            <mat-icon>delete</mat-icon>
          </button>
        </div>
        <div class="nested-content">
          @if (isDictionaryField(field)) {
            <div class="add-item-row">
              <mat-form-field appearance="outline" class="field-input">
                <mat-label>Add custom key</mat-label>
                <input matInput [(ngModel)]="newCustomKeys[field.path]" [ngModelOptions]="{standalone: true}"
                  (keyup.enter)="addCustomKey(field)" placeholder="e.g. myCustomField" />
              </mat-form-field>
              <button mat-icon-button color="primary" (click)="addCustomKey(field)" [disabled]="!newCustomKeys[field.path]">
                <mat-icon>add</mat-icon>
              </button>
            </div>
          }
          @for (child of field.children; track child; let j = $index) {
            <ng-container *ngTemplateOutlet="schemaField; context: { field: child, index: j, fields: field.children }"></ng-container>
          }
        </div>
      </div>
    }
    @case ('primitiveArray') {
      <div class="nested-object">
        <div class="nested-header">
          <span class="nested-title">{{ field.key }} ({{ field.children?.length || 0 }})</span>
          <button mat-icon-button color="warn" (click)="removeSubField(fields, index)" matTooltip="Remove">
            <mat-icon>delete</mat-icon>
          </button>
        </div>
        <div class="nested-content">
          <ng-container *ngTemplateOutlet="primitiveArrayContent; context: { field: field }"></ng-container>
        </div>
      </div>
    }
    @case ('objectArray') {
      <div class="nested-object">
        <div class="nested-header">
          <span class="nested-title">{{ field.key }} ({{ field.children?.length || 0 }})</span>
          <div class="field-actions">
            <button mat-icon-button color="primary" (click)="addArrayItem(field)" matTooltip="Add item">
              <mat-icon>add</mat-icon>
            </button>
            <button mat-icon-button color="warn" (click)="removeSubField(fields, index)" matTooltip="Remove">
              <mat-icon>delete</mat-icon>
            </button>
          </div>
        </div>
        <div class="nested-content">
          @for (item of field.children; track item; let j = $index) {
            <ng-container *ngTemplateOutlet="objectArrayItem; context: { field: item, index: j, fields: field.children }"></ng-container>
          }
        </div>
      </div>
    }
  }
</ng-template>

<!-- Object array items (moveable) -->
<ng-template #objectArrayItem let-field="field" let-index="index" let-fields="fields">
  <div class="nested-object" [id]="'item-' + field.path" [class.highlighted]="highlightedPath === field.path">
    <div class="nested-header">
      <span class="nested-title">{{ field.key }}</span>
      <div class="field-actions">
        <button mat-icon-button (click)="moveFieldUp(fields, index)" [disabled]="index === 0" matTooltip="Move up">
          <mat-icon>arrow_upward</mat-icon>
        </button>
        <button mat-icon-button (click)="moveFieldDown(fields, index)" [disabled]="index === fields.length - 1" matTooltip="Move down">
          <mat-icon>arrow_downward</mat-icon>
        </button>
        <button mat-icon-button color="warn" (click)="removeSubField(fields, index)" matTooltip="Remove">
          <mat-icon>delete</mat-icon>
        </button>
      </div>
    </div>
    <div class="nested-content">
      @for (child of field.children; track child; let k = $index) {
        <ng-container *ngTemplateOutlet="schemaField; context: { field: child, index: k, fields: field.children }"></ng-container>
      }
    </div>
  </div>
</ng-template>

<mat-dialog-actions align="end">
  <button mat-stroked-button (click)="onCancel()">Cancel</button>
  <button mat-stroked-button color="primary" [disabled]="saving" (click)="onSave()">
    {{ saving ? 'Saving...' : 'Save' }}
  </button>
</mat-dialog-actions>
